import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/PasswordInput';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter username, 2: Enter token and new password
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [displayToken, setDisplayToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleRequestToken = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await api.post('/auth/forgot-password', { username });
      
      // Check if email was sent or token was returned
      if (response.data.email_sent) {
        toast.success('Reset token sent to your email!');
        setStep(2);
      } else if (response.data.reset_token) {
        // Development mode - token returned in response
        setDisplayToken(response.data.reset_token);
        toast.success('Reset token generated!');
        setStep(2);
      } else {
        toast.success(response.data.message);
        setStep(2);
      }
    } catch (error) {
      toast.error('Failed to generate reset token');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.post('/auth/reset-password-with-token', {
        username,
        reset_token: resetToken,
        new_password: newPassword
      });
      toast.success('Password reset successfully! Please login with your new password.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 relative overflow-hidden hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1684853848495-03a8121965f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxib29rc2hlbGYlMjBmaWxsZWQlMjB3aXRoJTIwYm9va3MlMjBjb3p5fGVufDB8fHx8MTc2NTk5OTUyMnww&ixlib=rb-4.1.0&q=85"
          alt="Bookshelf"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/80 flex items-center justify-center">
          <div className="text-center text-white px-8">
            <h1 className="text-5xl font-bold mb-4">The Bookshelf Project</h1>
            <p className="text-xl leading-relaxed max-w-2xl">Empowering families through literacy. Tracking the distribution of books and bookshelves to communities across DC, Maryland, and Virginia.</p>
          </div>
        </div>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <Card className="w-full max-w-md border-stone-200 shadow-floating" data-testid="forgot-password-card">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <KeyRound className="w-6 h-6 text-primary" />
              <CardTitle className="text-3xl">Reset Password</CardTitle>
            </div>
            <CardDescription>
              {step === 1 ? 'Enter your username to receive a reset token' : 'Enter the reset token and your new password'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 ? (
              <form onSubmit={handleRequestToken} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    data-testid="forgot-username-input"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="h-11"
                    placeholder="Enter your username"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="request-token-button"
                  className="w-full rounded-full"
                  disabled={loading}
                >
                  {loading ? 'Generating...' : 'Get Reset Token'}
                </Button>
                <Link to="/login" className="block">
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    data-testid="back-to-login"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                {displayToken ? (
                  <div className="p-4 bg-accent/20 border-2 border-accent rounded-lg" data-testid="token-display">
                    <p className="text-sm font-medium mb-2">Your Reset Token:</p>
                    <p className="text-3xl font-bold text-center tracking-widest mb-2">{displayToken}</p>
                    <p className="text-xs text-stone-600">This token will expire in 30 minutes. In production, this would be sent to your email.</p>
                  </div>
                ) : (
                  <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                    <p className="text-sm font-medium text-green-900 mb-2">✉️ Check Your Email</p>
                    <p className="text-xs text-green-700">A 6-digit reset token has been sent to the email address associated with your account. Please check your inbox (and spam folder).</p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="reset_token">Reset Token</Label>
                  <Input
                    id="reset_token"
                    data-testid="reset-token-input"
                    type="text"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    required
                    className="h-11 text-center tracking-widest text-xl"
                    placeholder="Enter 6-digit token"
                    maxLength={6}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="new_password">New Password</Label>
                  <PasswordInput
                    id="new_password"
                    data-testid="new-password-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="h-11"
                    minLength={6}
                    placeholder="Enter new password"
                  />
                  <p className="text-xs text-stone-500">Must be at least 6 characters</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm New Password</Label>
                  <PasswordInput
                    id="confirm_password"
                    data-testid="confirm-password-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="h-11"
                    minLength={6}
                    placeholder="Confirm new password"
                  />
                </div>
                
                <Button
                  type="submit"
                  data-testid="reset-password-button"
                  className="w-full rounded-full"
                  disabled={loading}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
                
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setStep(1);
                    setResetToken('');
                    setDisplayToken('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  data-testid="back-to-step1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Start Over
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}